import { ChevronRight } from "@untitledui/icons";
import { Focusable } from "react-aria-components";
import { SAFETY_POLICY_PRIORITY } from "@/api/safety-policies";
import { Tooltip } from "@/components/base/tooltip/tooltip";
import { TIKTOK_ADS_AUTH_REQUIRED } from "@/pages/safety-beta/permission-copy";
import { cx } from "@/utils/cx";
import { AuthoriseTikTokAdsButton } from "./authorise-tiktok-ads-button";
import { POLICY_DESCRIPTIONS, POLICY_ICONS, POLICY_NAMES, type PolicyType } from "./policy-copy";

export type PolicyListRow = {
  id: string;
  type: PolicyType;
  risksUsing: number;
  /** Omitted at workspace level, where the template owns no videos to run on. */
  fired30d?: number;
  /** Shown when a policy type has no display copy yet. */
  fallbackName?: string;
  /** True when the policy's only effect is a GMV Max action. Absent at workspace level. */
  gmvMaxOnly?: boolean;
};

type PolicyListCardProps = {
  rows: PolicyListRow[];
  onOpen: (id: string) => void;
  /** Whether a `gmvMaxOnly` row renders as blocked — never true at workspace level. */
  adsBlocked?: boolean;
};

/**
 * The "Response" section of the Policies tab: every policy as a row. Shared by the
 * merchant screen and the workspace template so both stay visually identical.
 */
export const PolicyListCard = ({ rows, onOpen, adsBlocked }: PolicyListCardProps) => {
  // Order by enforcement severity — most severe policy first.
  const sorted = [...rows].sort((a, b) => SAFETY_POLICY_PRIORITY[a.type] - SAFETY_POLICY_PRIORITY[b.type]);
  const showRun = sorted.some((row) => row.fired30d !== undefined);

  return (
    <div className="flex flex-col gap-4">
      <h2 className="text-base font-medium text-secondary">Response</h2>
      <div className="rounded-md border border-border-primary bg-white overflow-hidden">
        {/* Column headers */}
        <div className="flex items-center gap-4 px-5 py-3 border-b border-border-secondary bg-white">
          <span className="flex-1 text-xs font-semibold text-quaternary">Policy</span>
          <span className="w-42 shrink-0 text-right text-xs font-semibold text-quaternary">Risks using policy</span>
          {showRun && <span className="w-24 shrink-0 text-right text-xs font-semibold text-quaternary">Run (30d)</span>}
          <span className="w-4 shrink-0" />
        </div>

        {sorted.map((row) => (
          <PolicyRow
            key={row.id}
            row={row}
            showRun={showRun}
            blocked={!!row.gmvMaxOnly && !!adsBlocked}
            onOpen={() => onOpen(row.id)}
          />
        ))}
      </div>
    </div>
  );
};

const PolicyRow = ({
  row,
  showRun,
  blocked,
  onOpen,
}: {
  row: PolicyListRow;
  showRun: boolean;
  blocked: boolean;
  onOpen: () => void;
}) => {
  // A policy counts as active when at least one risk is mapped to it.
  const active = row.risksUsing > 0;
  const name = POLICY_NAMES[row.type] ?? row.fallbackName;
  const description = POLICY_DESCRIPTIONS[row.type] ?? "";
  const Icon = POLICY_ICONS[row.type];

  const nameAndDescription = (
    <div className="flex-1 flex items-center gap-3 min-w-0">
      <Icon
        aria-hidden="true"
        className={cx("w-[18px] h-[18px] shrink-0 mb-0.5", active ? "text-secondary" : "text-tertiary")}
      />
      <div className="min-w-0">
        <p className={cx("text-base font-medium leading-snug", active ? "text-primary" : "text-tertiary")}>{name}</p>
        {description && <p className="text-sm text-tertiary mt-0.5 leading-relaxed">{description}</p>}
      </div>
    </div>
  );

  // A <div> rather than a <button>: blocked rows nest the authorise CTA, another button, inside.
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onOpen();
        }
      }}
      className="w-full flex items-center gap-4 pl-6 pr-5 py-5 border-b border-border-secondary last:border-b-0 hover:bg-secondary transition-colors cursor-pointer text-left"
    >
      {/* Focusable claims the trigger ref for Tooltip — without it, it renders pinned to the viewport corner. */}
      {blocked ? (
        <Tooltip {...TIKTOK_ADS_AUTH_REQUIRED}>
          <Focusable isDisabled>{nameAndDescription}</Focusable>
        </Tooltip>
      ) : (
        nameAndDescription
      )}

      {blocked ? (
        <>
          <span className="w-42 shrink-0" />
          {showRun && <span className="w-24 shrink-0" />}
          <div onClick={(event) => event.stopPropagation()}>
            <AuthoriseTikTokAdsButton />
          </div>
        </>
      ) : (
        <>
          <span className="w-42 shrink-0 text-right text-sm text-tertiary tabular-nums">{row.risksUsing}</span>
          {showRun && (
            <span className="w-24 shrink-0 text-right text-sm text-tertiary tabular-nums">{row.fired30d}</span>
          )}
          <ChevronRight aria-hidden="true" className="w-4 h-4 text-quaternary shrink-0" />
        </>
      )}
    </div>
  );
};
